import type { MouseEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'

import css from './Category.module.css'

import { type CategoryTypes, CategoryList } from '#/local/component/CategoryList'

import { fetchCategoryIdListByQuery, fetchCategoryListByIds } from '#/local/repository/Category'
import { sendReport } from '#/local/repository/Report'

import { Config } from '#/global/helper/Config'
import { linkToCategoryEshop, linkToCategoryRental } from '#/global/helper/LinkTo'
import { MessageFlash } from '#/global/helper/MessageFlash'

export const Category = () => {
    const lang = Config.getLang()
    const [searchParameters, setSearchParameters] = useSearchParams()
    const query = searchParameters.get('query')

    const eshopPage = Number(searchParameters.get('eshopPage')) || 1
    const rentalPage = Number(searchParameters.get('rentalPage')) || 1

    const eshopNodesIds = useQuery({
        queryKey: ['categoryIds eshop', { eshopPage, query }],
        queryFn: async () => fetchCategoryIdListByQuery({ query: query!, page: eshopPage, type: 'eshop' }),
        enabled: !!query,
    })

    const eshopNodes = useQuery({
        queryKey: ['category eshop', { idList: eshopNodesIds.data?.list }],
        queryFn: async () => fetchCategoryListByIds(eshopNodesIds.data!.list, 'eshop'),
        enabled: eshopNodesIds.isSuccess && !!eshopNodesIds.data?.itemsCount,
    })

    const rentalNodesIds = useQuery({
        queryKey: ['categoryIds rental', { rentalPage, query }],
        queryFn: async () => fetchCategoryIdListByQuery({ query: query!, page: rentalPage, type: 'rental' }),
        enabled: !!query,
    })

    const rentalNodes = useQuery({
        queryKey: ['category rental', { idList: rentalNodesIds.data?.list }],
        queryFn: async () => fetchCategoryListByIds(rentalNodesIds.data!.list, 'rental'),
        enabled: rentalNodesIds.isSuccess && !!rentalNodesIds.data?.itemsCount,
    })

    const handleSetPageEshop = (page: number) => setPage('eshop', page)
    const handleSetPageRental = (page: number) => setPage('rental', page)

    const setPage = (type: CategoryTypes, page: number) => {
        const urlParameters = new URLSearchParams(window.location.search)
        urlParameters.set(`${type}Page`, page.toString())
        setSearchParameters(urlParameters)
    }

    if (eshopNodesIds.isError || rentalNodesIds.isError || eshopNodes.isError || rentalNodes.isError) {
        MessageFlash.add({
            message: 'Nastala chyba při načítání kategorií.',
            type: 'error',
        })
    }

    const handleSendEshopReport = (event: MouseEvent<HTMLAnchorElement>, { code, slug }: { code: string, slug: string }) => {
        event.preventDefault()
        const link = linkToCategoryEshop(code, slug)
        query && void sendReport(query, 'nodes-eshop', code, link)
    }

    const handleSendRentalReport = (event: MouseEvent<HTMLAnchorElement>, { code, slug }: { code: string, slug: string }) => {
        event.preventDefault()
        const link = linkToCategoryRental(code, slug)
        query && void sendReport(query, 'nodes-rental', code, link)
    }

    return (
        <div className={css.container}>
            {(eshopNodes.isLoading || eshopNodesIds.isLoading) && <CategoryList loadingState />}
            {eshopNodes.isSuccess && eshopNodes.data.length > 0 && eshopNodesIds.isSuccess && (
                <CategoryList
                    itemsCount={eshopNodesIds.data.itemsCount}
                    lang={lang}
                    list={eshopNodes.data}
                    onItemClick={handleSendEshopReport}
                    page={eshopPage}
                    setPage={handleSetPageEshop}
                    type="eshop"
                />
            )}
            {rentalNodes.isSuccess && rentalNodes.data.length > 0 && rentalNodesIds.isSuccess && (
                <CategoryList
                    itemsCount={rentalNodesIds.data.itemsCount}
                    lang={lang}
                    list={rentalNodes.data}
                    onItemClick={handleSendRentalReport}
                    page={rentalPage}
                    setPage={handleSetPageRental}
                    type="rental"
                />
            )}
        </div>
    )
}
